import { createApp } from "vue";
import { currency } from "@/utils/currency";
import { closeFixedDropdowns, initFixedDropdowns } from "./utils/fixedDropdowns";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

import App from "./App.vue";
import router from "./router";
import i18n from "./i18n";
import { startDomTranslation } from "./i18n/dom";
import BootstrapVueNext from "bootstrap-vue-next";
import VueApexCharts from "vue3-apexcharts";
import { QuillEditor } from '@vueup/vue-quill'
import Vue3Prism from 'vue3-prism/lib/Vue3Prism.common.js'

import { globalHelper } from './utils/helpers'


import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import ToastComponent from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import Vue3EasyDataTable from 'vue3-easy-data-table';

import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

// Import specific icons
import { faUserSecret } from '@fortawesome/free-solid-svg-icons'
import { faFacebook } from '@fortawesome/free-brands-svg-icons'
import VueGoodTablePlugin from 'vue-good-table-next';
import Swal from 'sweetalert2'
// import { VueSpinnersPlugin } from 'vue3-spinners';


import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue-next/dist/bootstrap-vue-next.css";
import "swiper/css";
import "swiper/css/bundle";
import 'flatpickr/dist/flatpickr.css';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import '@vueup/vue-quill/dist/vue-quill.bubble.css';
import "vue3-prism/lib/Vue3Prism.css"
import 'vue3-easy-data-table/dist/style.css';
import 'vue-good-table-next/dist/vue-good-table-next.css';
import 'sweetalert2/src/sweetalert2.scss'

import 'vue-select/dist/vue-select.css';


import "./assets/custom.scss";
import { useAuthStore } from "./stores/auth";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import 'sweetalert2/dist/sweetalert2.min.css'
import PaginatorContent from "@/components/PaginatorContent.vue";
import TablePager from "@/components/TablePager.vue";
import AppLoader from "@/components/Common/AppLoader.vue";
import { setRouteLoading } from "./utils/loading";

import vSelect from 'vue-select'


// Add icons to the library
library.add(faUserSecret, faFacebook)


const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const app = createApp(App)
app.use(pinia);
app.use(router);
app.use(i18n);
app.use(VueApexCharts);
app.use(BootstrapVueNext);
app.use(PrimeVue);
app.use(ToastService);

app.use(Toast);
app.use(VueGoodTablePlugin);
// app.use(VueSpinnersPlugin);




// Register Toast globally
app.component('Toast', ToastComponent);
app.component('QuillEditor', QuillEditor)
.use(Vue3Prism)

app.component('EasyDataTable', Vue3EasyDataTable);
app.component('font-awesome-icon', FontAwesomeIcon)
app.component("PaginatorContent",   PaginatorContent);
app.component("TablePager", TablePager);
app.component("AppLoader", AppLoader);

// Top progress bar while a page (lazy chunk + guards) is loading.
router.beforeEach(() => {
  setRouteLoading(true);
});
router.afterEach(() => {
  setRouteLoading(false);
  closeFixedDropdowns(); // an open row-action menu must not follow us to the next page
});
router.onError(() => setRouteLoading(false));

app.component('v-select', vSelect)
// Add global function
app.config.globalProperties.$showSuccessToast = (message: string) => {
  const toast = useToast();
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: message,
    life: 3000,
  });
};

app.config.globalProperties.$showErrorToast = (message: string) => {
  const toast = useToast();
  toast.add({
    severity: 'error',
    summary: 'Erreur',
    detail: message,
    life: 3000,
  });
};

app.config.globalProperties.$globalHelper = globalHelper
// currency for the templates: {{ $devis }}
Object.defineProperty(app.config.globalProperties, '$devis', { get: () => currency() })

app.mount("#app");

// Row-action menus render above any scroll container (tables), see utils/fixedDropdowns.ts
initFixedDropdowns();
startDomTranslation();


// 🔥 Global permissions refresher
// The router guard loads permissions on the first navigation. After that,
// refresh them periodically, but only while the tab is visible, so idle
// background tabs don't send requests.
const auth = useAuthStore()
const PERMISSIONS_REFRESH_MS = 300000
let lastPermissionsRefresh = Date.now()

function refreshPermissions() {
  if (document.visibilityState !== "visible" || !auth.isAuthenticated) return
  lastPermissionsRefresh = Date.now()
  auth.fetchPermissions()
}

setInterval(refreshPermissions, PERMISSIONS_REFRESH_MS)

document.addEventListener("visibilitychange", () => {
  if (Date.now() - lastPermissionsRefresh >= PERMISSIONS_REFRESH_MS) {
    refreshPermissions()
  }
})

// The session is shared by all tabs through localStorage. If another tab
// logs out or logs in as a different user, reload this tab so it doesn't
// keep using the old session.
window.addEventListener("storage", (event) => {
  if (event.key !== "auth") return
  let nextToken: string | null = null
  try {
    nextToken = event.newValue ? JSON.parse(event.newValue).token ?? null : null
  } catch {
    nextToken = null
  }
  if (nextToken !== auth.token) {
    window.location.reload()
  }
})
